import React, { useState, useEffect } from 'react'
import { 
  Upload, 
  File, 
  Image, 
  Video, 
  Music, 
  FileText, 
  Download, 
  Trash2, 
  Eye,
  AlertCircle,
  CheckCircle,
  Cloud,
  HardDrive
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { apiRequest } from '@/services/api'
import toast from 'react-hot-toast'

const FileManager = () => {
  const { user } = useAuth()
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [storageConfig, setStorageConfig] = useState(null)
  const [selectedFiles, setSelectedFiles] = useState([])

  useEffect(() => {
    fetchFiles()
    fetchStorageConfig()
  }, [])

  const fetchFiles = async () => {
    try {
      setLoading(true)
      const response = await apiRequest.get('/files/')
      // API returns FileSearchResponse with files array
      setFiles(response.data.files || [])
    } catch (error) {
      console.error('Error fetching files:', error)
      toast.error('Failed to load files')
    } finally {
      setLoading(false)
    }
  }

  const fetchStorageConfig = async () => {
    try {
      const response = await apiRequest.get('/system-settings/storage/config')
      setStorageConfig(response.data)
    } catch (error) {
      console.error('Error fetching storage config:', error)
    }
  }

  const handleFileUpload = async (event) => {
    const uploadFiles = Array.from(event.target.files)
    if (uploadFiles.length === 0) return

    setUploading(true)
    
    try {
      for (const file of uploadFiles) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('is_public', 'true')  // Make files public for easier access
        formData.append('generate_thumbnail', 'true')

        const response = await apiRequest.post('/files/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        toast.success(`${file.name} uploaded successfully`)
      }

      // Refresh file list
      await fetchFiles()
    } catch (error) {
      console.error('Error uploading files:', error)
      toast.error(error.response?.data?.detail || 'Failed to upload files')
    } finally {
      setUploading(false)
      // Reset file input
      event.target.value = ''
    }
  }

  const handleFileDownload = async (fileId, filename) => {
    try {
      const response = await apiRequest.get(`/files/${fileId}/download`, {
        responseType: 'blob',
      })

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading file:', error)
      toast.error('Failed to download file')
    }
  }

  const handleFileDelete = async (fileId) => {
    if (!confirm('Are you sure you want to delete this file?')) return

    try {
      await apiRequest.delete(`/files/${fileId}`)
      toast.success('File deleted successfully')
      await fetchFiles()
    } catch (error) {
      console.error('Error deleting file:', error)
      toast.error('Failed to delete file')
    }
  }

  const getFileIcon = (fileType, mimeType) => {
    switch (fileType) {
      case 'image':
        return <Image className="h-5 w-5 text-blue-500" />
      case 'video':
        return <Video className="h-5 w-5 text-purple-500" />
      case 'audio':
        return <Music className="h-5 w-5 text-green-500" />
      case 'document':
        return <FileText className="h-5 w-5 text-orange-500" />
      default:
        return <File className="h-5 w-5 text-gray-500" />
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getStorageIcon = () => {
    if (!storageConfig) return <HardDrive className="h-4 w-4" />
    
    switch (storageConfig.storage_backend) {
      case 'gcs':
        return <Cloud className="h-4 w-4 text-blue-500" />
      case 's3':
        return <Cloud className="h-4 w-4 text-orange-500" />
      default:
        return <HardDrive className="h-4 w-4 text-gray-500" />
    }
  }

  const getStorageLabel = () => {
    if (!storageConfig) return 'Local Storage'
    
    switch (storageConfig.storage_backend) {
      case 'gcs':
        return 'Google Cloud Storage'
      case 's3':
        return 'Amazon S3'
      default:
        return 'Local Storage'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 flex items-center">
                <File className="h-6 w-6 text-gray-400 mr-3" />
                File Manager
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Upload and manage your files
              </p>
            </div>
            
            {/* Storage Backend Info */}
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              {getStorageIcon()}
              <span>{getStorageLabel()}</span>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Upload Area */}
          <div className="mb-8">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-4">
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="mt-2 block text-sm font-medium text-gray-900">
                    {uploading ? 'Uploading...' : 'Upload files'}
                  </span>
                  <span className="mt-1 block text-sm text-gray-500">
                    Click to select files or drag and drop
                  </span>
                </label>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  multiple
                  className="sr-only"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
              </div>
            </div>
          </div>

          {/* Files List */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Your Files ({files.length})
            </h2>
            
            {files.length === 0 ? (
              <div className="text-center py-12">
                <File className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No files</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Upload your first file to get started.
                </p>
              </div>
            ) : (
              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                  {files.map((file) => (
                    <li key={file.id}>
                      <div className="px-4 py-4 flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            {getFileIcon(file.file_type, file.mime_type)}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {file.original_filename}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatFileSize(file.file_size)} • {file.mime_type}
                            </div>
                            <div className="text-xs text-gray-400 flex items-center mt-1">
                              {file.storage_backend === 'gcs' ? (
                                <>
                                  <Cloud className="h-3 w-3 mr-1" />
                                  Google Cloud Storage
                                </>
                              ) : (
                                <>
                                  <HardDrive className="h-3 w-3 mr-1" />
                                  Local Storage
                                </>
                              )}
                              <span className="ml-2">•</span>
                              <span className="ml-2">
                                {new Date(file.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {/* Status */}
                          <div className="flex items-center">
                            {file.status === 'processed' ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : file.status === 'failed' ? (
                              <AlertCircle className="h-4 w-4 text-red-500" />
                            ) : (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <button
                            onClick={() => handleFileDownload(file.id, file.original_filename)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          
                          <button
                            onClick={() => handleFileDelete(file.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FileManager
